import React from "react";
import FractionInput from "../../../../components/inputs/FractionInput";
import { InlineMath, BlockMath } from "react-katex";

// CountFromGivenElementTask.js
// Zadanie 2: dana jest jedna z wartości k, s lub w – policz jedną z pozostałych.
// k – liczba krawędzi, s – liczba ścian, w – liczba wierzchołków, n – liczba boków podstawy

export function generateCountFromGivenElementTask() {
  const n = Math.floor(Math.random() * 5) + 3; // 3–7 boków

  const baseNames = {
    3: "trójkąta",
    4: "czworokąta",
    5: "pięciokąta",
    6: "sześciokąta",
    7: "siedmiokąta",
  };
  const baseName = baseNames[n] || `${n}-kąta`;

  const types = ["k", "s", "w"]; // k - krawędzie, s - ściany, w - wierzchołki

  const knownType = types[Math.floor(Math.random() * types.length)];
  const remaining = types.filter((t) => t !== knownType);
  const targetType = remaining[Math.floor(Math.random() * remaining.length)];

  let givenValue;
  if (knownType === "k") givenValue = 3 * n; // k = 3n
  if (knownType === "s") givenValue = n + 2; // s = n + 2
  if (knownType === "w") givenValue = 2 * n; // w = 2n

  const core = {
    n,
    knownType,
    targetType,
    givenValue,
    baseName,
  };

  // Zwracamy zarówno płaski obiekt (stary tryb zadań),
  // jak i pole data (używane przez quiz / UniversalQuizRunner).
  return {
    type: "CountFromGivenElementTask",
    ...core,
    data: core,
  };
}

// Alias w stylu innych generatorów używanych w quizie
export function generateTask() {
  return generateCountFromGivenElementTask();
}

// Pomocniczo normalizujemy wejście – może to być albo cały task,
// albo obiekt w stylu { data: {...} }.
function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

export function renderTask(taskOrWrapper) {
  // Obsłuży zarówno wersję z unwrapTask, jak i starą, „płaską”
  const task =
    typeof unwrapTask === "function" ? unwrapTask(taskOrWrapper) : taskOrWrapper;

  const { knownType, targetType, givenValue } = task;

  const labels = {
    k: "krawędzi",
    s: "ścian",
    w: "wierzchołków",
  };

  return (
    <p>
      Dany jest graniastosłup, którego liczba {labels[knownType]} wynosi{" "}
      <InlineMath math={String(givenValue)} />. Oblicz liczbę{" "}
      {labels[targetType]}.
    </p>
  );
}


export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  const task = unwrapTask(taskOrWrapper);
  const { targetType } = task;

  let label = "k";
  if (targetType === "s") label = "s";
  if (targetType === "w") label = "w";

  return (
    <div className="flex items-center gap-2">
      <InlineMath math={`${label} =`} />
      <FractionInput
        value={userAnswer || ""}
        onChange={(val) => onChange(val)}
        disabled={disabled}
        label={label}
      />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  if (!raw && raw !== 0) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={String(raw)} />;
}

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);
  const { n, targetType } = task;

  let correct;
  if (targetType === "k") correct = 3 * n;
  if (targetType === "s") correct = n + 2;
  if (targetType === "w") correct = 2 * n;

  let given = null;

  if (userAnswer && typeof userAnswer === "object") {
    if (typeof userAnswer.parsed === "number") {
      given = userAnswer.parsed;
    } else if (typeof userAnswer.raw !== "undefined") {
      const num = Number(
        String(userAnswer.raw).replace(",", ".")
      );
      if (!Number.isNaN(num)) given = num;
    }
  } else if (typeof userAnswer !== "undefined") {
    const num = Number(String(userAnswer).replace(",", "."));
    if (!Number.isNaN(num)) given = num;
  }

  if (given === null) return false;

  return Math.abs(given - correct) < 0.01;
}

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { n, targetType } = task;

  if (targetType === "k") {
    // k – liczba krawędzi
    return <InlineMath math={`k = ${3 * n}`} />;
  }

  if (targetType === "s") {
    // s – liczba ścian
    return <InlineMath math={`s = ${n + 2}`} />;
  }

  if (targetType === "w") {
    // w – liczba wierzchołków
    return <InlineMath math={`w = ${2 * n}`} />;
  }

  return null;
}

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const { n, knownType, targetType, givenValue } = task;
  if (n == null || !knownType || !targetType || givenValue == null) return null;

  const kVal = 3 * n;
  const sVal = n + 2;
  const wVal = 2 * n;

  // 1) znamy k, szukamy s
  if (knownType === "k" && targetType === "s") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę krawędzi{" "}
          <InlineMath math="k" /> graniastosłupa o podstawie n-kąta.
        </p>
        <BlockMath math="k = 3n" />

        <p>
          <strong>Krok 2:</strong> Przekształcamy wzór tak, aby wyznaczyć{" "}
          <InlineMath math="n" />.
        </p>
       <BlockMath math={"n = \\dfrac{k}{3}"} />

        <p>
          <strong>Krok 3:</strong> Podstawiamy{" "}
          <InlineMath math={`k = ${kVal}`} />.
        </p>
        <BlockMath math={`n = \\dfrac{${kVal}}{3} = ${n}`} />

        <p>
          <strong>Krok 4:</strong> Zapisujemy wzór na liczbę ścian{" "}
          <InlineMath math="s" /> graniastosłupa:
        </p>
        <BlockMath math="s = n + 2" />

        <p>
          <strong>Krok 5:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${n}`} />.
        </p>
        <BlockMath math={`s = ${n} + 2 = ${sVal}`} />
      </>
    );
  }

  // 2) znamy k, szukamy w
  if (knownType === "k" && targetType === "w") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę krawędzi{" "}
          <InlineMath math="k" /> graniastosłupa o podstawie n-kąta.
        </p>
        <BlockMath math="k = 3n" />

        <p>
          <strong>Krok 2:</strong> Przekształcamy wzór tak, aby wyznaczyć{" "}
          <InlineMath math="n" />.
        </p>
        <BlockMath math={"n = \\dfrac{k}{3}"} />

        <p>
          <strong>Krok 3:</strong> Podstawiamy{" "}
          <InlineMath math={`k = ${kVal}`} />.
        </p>
        <BlockMath math={`n = \\dfrac{${kVal}}{3} = ${n}`} />

        <p>
          <strong>Krok 4:</strong> Zapisujemy wzór na liczbę wierzchołków{" "}
          <InlineMath math="w" /> graniastosłupa:
        </p>
        <BlockMath math="w = 2n" />

        <p>
          <strong>Krok 5:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${n}`} />.
        </p>
        <BlockMath math={`w = 2 \\cdot ${n} = ${wVal}`} />
      </>
    );
  }

  // 3) znamy s, szukamy k
  if (knownType === "s" && targetType === "k") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę ścian{" "}
          <InlineMath math="s" /> graniastosłupa o podstawie n-kąta.
        </p>
        <BlockMath math="s = n + 2" />

        <p>
          <strong>Krok 2:</strong> Przekształcamy wzór tak, aby wyznaczyć{" "}
          <InlineMath math="n" />.
        </p>
        <BlockMath math="n = s - 2" />

        <p>
          <strong>Krok 3:</strong> Podstawiamy{" "}
          <InlineMath math={`s = ${sVal}`} />.
        </p>
        <BlockMath math={`n = ${sVal} - 2 = ${n}`} />

        <p>
          <strong>Krok 4:</strong> Zapisujemy wzór na liczbę krawędzi{" "}
          <InlineMath math="k" /> graniastosłupa:
        </p>
        <BlockMath math="k = 3n" />

        <p>
          <strong>Krok 5:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${n}`} />.
        </p>
        <BlockMath math={`k = 3 \\cdot ${n} = ${kVal}`} />
      </>
    );
  }

  // 4) znamy s, szukamy w
  if (knownType === "s" && targetType === "w") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę ścian{" "}
          <InlineMath math="s" /> graniastosłupa o podstawie n-kąta.
        </p>
        <BlockMath math="s = n + 2" />

        <p>
          <strong>Krok 2:</strong> Przekształcamy wzór tak, aby wyznaczyć{" "}
          <InlineMath math="n" />.
        </p>
        <BlockMath math="n = s - 2" />

        <p>
          <strong>Krok 3:</strong> Podstawiamy{" "}
          <InlineMath math={`s = ${sVal}`} />.
        </p>
        <BlockMath math={`n = ${sVal} - 2 = ${n}`} />

        <p>
          <strong>Krok 4:</strong> Zapisujemy wzór na liczbę wierzchołków{" "}
          <InlineMath math="w" /> graniastosłupa:
        </p>
        <BlockMath math="w = 2n" />

        <p>
          <strong>Krok 5:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${n}`} />.
        </p>
        <BlockMath math={`w = 2 \\cdot ${n} = ${wVal}`} />
      </>
    );
  }

  // 5) znamy w, szukamy k
  if (knownType === "w" && targetType === "k") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę wierzchołków{" "}
          <InlineMath math="w" /> graniastosłupa o podstawie n-kąta.
        </p>
        <BlockMath math="w = 2n" />

        <p>
          <strong>Krok 2:</strong> Przekształcamy wzór tak, aby wyznaczyć{" "}
          <InlineMath math="n" />.
        </p>
        <BlockMath math={"n = \\dfrac{w}{2}"} />

        <p>
          <strong>Krok 3:</strong> Podstawiamy{" "}
          <InlineMath math={`w = ${wVal}`} />.
        </p>
        <BlockMath math={`n = \\dfrac{${wVal}}{2} = ${n}`} />

        <p>
          <strong>Krok 4:</strong> Zapisujemy wzór na liczbę krawędzi{" "}
          <InlineMath math="k" /> graniastosłupa:
        </p>
        <BlockMath math="k = 3n" />

        <p>
          <strong>Krok 5:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${n}`} />.
        </p>
        <BlockMath math={`k = 3 \\cdot ${n} = ${kVal}`} />
      </>
    );
  }

  // 6) znamy w, szukamy s
  if (knownType === "w" && targetType === "s") {
    return (
      <>
        <p>
          <strong>Krok 1:</strong> Zapisujemy wzór na liczbę wierzchołków{" "}
          <InlineMath math="w" /> graniastosłupa o podstawie n-kąta.
        </p>
        <BlockMath math="w = 2n" />

        <p>
          <strong>Krok 2:</strong> Przekształcamy wzór tak, aby wyznaczyć{" "}
          <InlineMath math="n" />.
        </p>
        <BlockMath math={"n = \\dfrac{w}{2}"} />

        <p>
          <strong>Krok 3:</strong> Podstawiamy{" "}
          <InlineMath math={`w = ${wVal}`} />.
        </p>
        <BlockMath math={`n = \\dfrac{${wVal}}{2} = ${n}`} />

        <p>
          <strong>Krok 4:</strong> Zapisujemy wzór na liczbę ścian{" "}
          <InlineMath math="s" /> graniastosłupa:
        </p>
        <BlockMath math="s = n + 2" />

        <p>
          <strong>Krok 5:</strong> Podstawiamy{" "}
          <InlineMath math={`n = ${n}`} />.
        </p>
        <BlockMath math={`s = ${n} + 2 = ${sVal}`} />
      </>
    );
  }

  return null;
}
